import { useQuery } from "@tanstack/react-query"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { getAgentPerformance } from "../api/endpoints/agents"
import { getDashboardMyPerformance, getDashboardMyWork } from "../api/endpoints/dashboard"
import {
  CardSkeleton,
  DataTable,
  EmptyState,
  PageSection,
  StatCard,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "../components/ui"
import { useAuth } from "../stores/auth"
import { formatCurrency, formatDate } from "../utils/formatting"

function targetStatusTone(percentage: number): "success" | "warning" | "danger" {
  if (percentage >= 0.9) return "success"
  if (percentage >= 0.6) return "warning"
  return "danger"
}

function targetStatusLabel(percentage: number): string {
  if (percentage >= 0.9) return "On Track"
  if (percentage >= 0.6) return "At Risk"
  return "Behind"
}

export function AgentPerformancePage() {
  const { user } = useAuth()
  const agentId = user?.userId ?? ""

  const performanceQuery = useQuery({
    queryKey: ["agent-performance", agentId],
    queryFn: () => getAgentPerformance(agentId),
    enabled: Boolean(agentId),
  })

  const myPerformanceQuery = useQuery({
    queryKey: ["dashboard-my-performance"],
    queryFn: () => getDashboardMyPerformance({}),
    enabled: Boolean(agentId),
  })

  const myWorkQuery = useQuery({
    queryKey: ["dashboard-my-work"],
    queryFn: () => getDashboardMyWork(),
    enabled: Boolean(agentId),
  })

  const performance = performanceQuery.data?.data
  const target = performance?.activeTarget
  const history = myPerformanceQuery.data?.data?.history ?? []
  const pipelineSummary = myWorkQuery.data?.data?.pipelineSummary ?? []

  const targetProgress = target
    ? target.requiredAmount > 0
      ? Math.min((target.recoveredAmount ?? 0) / target.requiredAmount, 1)
      : 0
    : 0

  const daysRemaining = target?.endDate
    ? Math.max(0, Math.ceil((new Date(target.endDate).getTime() - Date.now()) / 86_400_000))
    : null

  return (
    <div className="flex flex-col gap-5">
      {/* Current Target Card */}
      <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <h3 className="text-sm font-bold text-slate-800 mb-4">Current Recovery Target</h3>
        {performanceQuery.isLoading ? (
          <CardSkeleton />
        ) : target ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-2xl font-bold text-slate-900">
                {Math.round(target.targetPercentage * 100)}% target
              </span>
              <StatusPill tone={targetStatusTone(targetProgress) === "success" ? "success" : targetStatusTone(targetProgress) === "warning" ? "warning" : "danger"}>
                {targetStatusLabel(targetProgress)}
              </StatusPill>
              {daysRemaining !== null && (
                <span className="text-sm text-slate-500">{daysRemaining} days remaining</span>
              )}
            </div>
            <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  targetProgress >= 0.9
                    ? "bg-emerald-500"
                    : targetProgress >= 0.6
                      ? "bg-amber-500"
                      : "bg-red-500"
                }`}
                style={{ width: `${Math.round(targetProgress * 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-slate-600">
              <span>
                Recovered: {formatCurrency(target.recoveredAmount ?? 0)}
              </span>
              <span>
                Required: {formatCurrency(target.requiredAmount ?? 0)}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No active recovery target</p>
        )}
      </section>

      {/* Activity Summary Cards */}
      <PageSection title="Activity summary">
        {performanceQuery.isLoading ? (
          <CardSkeleton />
        ) : (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard label="Assigned clients" value={performance?.assignedClients ?? 0} />
            <StatCard
              label="Outstanding"
              value={formatCurrency(performance?.outstandingAmount ?? 0)}
            />
            <StatCard
              label="Recovered"
              value={formatCurrency(performance?.recoveredAmount ?? 0)}
            />
            <StatCard label="Interactions logged" value={performance?.interactionsLogged ?? 0} />
            <StatCard label="Clients resolved" value={performance?.clientsResolved ?? 0} />
          </div>
        )}
      </PageSection>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Recovery Trend Chart */}
        <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <div className="mb-4">
            <h3 className="text-sm font-bold text-slate-800">Recovery Trend</h3>
            <p className="text-xs text-slate-500 mt-1">Daily recovered amount and interactions logged</p>
          </div>
          {myPerformanceQuery.isLoading ? (
            <CardSkeleton />
          ) : history.length === 0 ? (
            <EmptyState title="No history" description="Performance history will appear after daily rollups." />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="metricDate" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line dataKey="recoveredAmount" stroke="#0f766e" strokeWidth={2} name="Recovered" />
                  <Line dataKey="interactionsLogged" stroke="#2563eb" strokeWidth={2} name="Interactions" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Pipeline Distribution Chart */}
        <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
          <div className="mb-4">
            <h3 className="text-sm font-bold text-slate-800">Pipeline Distribution</h3>
            <p className="text-xs text-slate-500 mt-1">Current workload by pipeline status</p>
          </div>
          {myWorkQuery.isLoading ? (
            <CardSkeleton />
          ) : pipelineSummary.length === 0 ? (
            <EmptyState title="No pipeline data" description="Pipeline distribution will appear once clients are assigned." />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineSummary}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="pipelineStatus" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="loanCount" fill="#0f766e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      {/* Historical Performance Table */}
      <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-5">
        <div className="mb-4">
          <h3 className="text-sm font-bold text-slate-800">Historical Performance</h3>
          <p className="text-xs text-slate-500 mt-1">Daily metric rollups</p>
        </div>
        {myPerformanceQuery.isLoading ? (
          <CardSkeleton />
        ) : history.length === 0 ? (
          <EmptyState title="No history" description="Historical metrics will appear after daily rollups." />
        ) : (
          <DataTable>
            <TableHead>
              <Th>Date</Th>
              <Th>Recovered</Th>
              <Th>Interactions</Th>
              <Th>Clients Resolved</Th>
              <Th>Outstanding</Th>
            </TableHead>
            <TableBody>
              {history.map((row: any) => (
                <TableRow key={row.metricDate}>
                  <Td>{formatDate(row.metricDate)}</Td>
                  <Td>{formatCurrency(row.recoveredAmount ?? 0)}</Td>
                  <Td>{row.interactionsLogged ?? 0}</Td>
                  <Td>{row.clientsResolved ?? 0}</Td>
                  <Td>{formatCurrency(row.outstandingAmount ?? 0)}</Td>
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        )}
      </section>
    </div>
  )
}
